import { Activity, CSSProperties, useCallback, useMemo, useState } from 'react';

import { FontPack, ThemePack } from '@/types/theme-packs.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Text from '@ui/text/text/text';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import CustomCarousel from '@ui/custom-carousel/custom-carousel';
import classNames from 'classnames';
import { Components } from 'react-virtuoso';
import { TCustomCarouselScrollTo } from '@ui/custom-carousel/custom-carousel.types';
import AddFontCard from './theme-pack-add-font-card';
import OptionalComponent from '@ui/optional-component/optional-component';
import AddNewFont from './theme-pack-add-new-font';
import { useLoadFontPacks } from '@/connection/hooks/use-load-font-packs';
import { getActiveChannel } from '@/store/slices/user/selectors';
import WaitingIndicator from '@ui/waiting-indicator/waiting-indicator';
import { appendFontPacks } from '@/store/slices/templates';
import { selectLoadingFontPacks, selectFontPacks } from '@/store/slices/templates/selectors';

type TThemePackFontVariantChangeProps = {
  themePack?: ThemePack;
  disabled?: boolean;
  onThemePackUpdate?: (pack: ThemePack) => void;
  scrollToActiveFontPack?: boolean;
};

const ThemePackFontVariantChange = ({
  themePack,
  onThemePackUpdate,
  scrollToActiveFontPack = false,
}: TThemePackFontVariantChangeProps) => {
  const dispatch = useAppDispatch();
  const channel = useAppSelector(getActiveChannel);
  const [selectFontsOpen, setSelectFontsOpen] = useState<boolean>(false);
  const loadingFontPacks = useAppSelector(selectLoadingFontPacks);
  const fontPacks = useAppSelector(selectFontPacks);

  useLoadFontPacks(channel);

  const handleFontChange = useCallback(
    (result: FontPack) => {
      if (!themePack) return;

      const newTheme = {
        ...themePack,
        font_pack: result,
      };

      onThemePackUpdate?.(newTheme);
    },
    [themePack, onThemePackUpdate]
  );

  const activeFontPack = useMemo(() => {
    return themePack?.font_pack;
  }, [themePack]);

  // we need to customize the actual list (not wrapper)
  const List: Components['List'] = ({ style, children, ref }) => {
    const customStyles = {
      ...style,
      display: 'flex',
    };
    return (
      <div style={customStyles} ref={ref}>
        {children}
      </div>
    );
  };

  const scrollToIndex: () => TCustomCarouselScrollTo | undefined = useCallback(() => {
    if (!scrollToActiveFontPack || !fontPacks?.length || !activeFontPack) {
      return undefined;
    }
    const index = fontPacks.findIndex((pack) => pack.font_pack === activeFontPack?.font_pack);
    return {
      index,
      align: 'center',
    };
  }, [activeFontPack, fontPacks, scrollToActiveFontPack]);

  const handleFontPackCreated = (fontPack: FontPack) => {
    dispatch(appendFontPacks(fontPack));
    handleFontChange(fontPack);
    setSelectFontsOpen(false);
  };

  const cancelFontPackAdd = () => {
    setSelectFontsOpen(false);
  };

  if (!themePack || !fontPacks) return null;
  return (
    <Container width="100%" height="64px" className="position-relative flex fd-row">
      <Activity mode={selectFontsOpen ? 'hidden' : 'visible'}>
        <AddFontCard onClick={() => setSelectFontsOpen(true)} />
        <Container className="margin-h-16">
          <Container height="64px" borderWidth="1px" borderColor="var(--gray-900)"></Container>
        </Container>
        <OptionalComponent display={loadingFontPacks}>
          <WaitingIndicator />
        </OptionalComponent>
        <OptionalComponent display={!loadingFontPacks}>
          <CustomCarousel
            virtualized
            scrollTo={scrollToIndex()}
            list={fontPacks.map((fontPack) => ({ id: fontPack.font_pack, ...fontPack }))}
            customComponents={{ List }}
            itemContainerDimensions={{ width: '148px' }}
            renderItem={(fontPack) => {
              const isActive = activeFontPack?.font_pack === fontPack.font_pack;
              return (
                <Button
                  key={fontPack.font_pack}
                  value={fontPack.font_pack}
                  backgroundColor="var(--gray-800)"
                  hoverBackgroundColor="var(--gray-700)"
                  style={
                    {
                      '--button-border': isActive ? '2px solid var(--gray-100)' : '2px solid transparent',
                    } as CSSProperties
                  }
                  className="border-solid border-color border-color-transition clear-button text-left padding-2 radius-8 overflow-hidden theme-pack-carousel-item"
                  width="100%"
                  maxWidth="140px"
                  minWidth="140px"
                  height="64px"
                  disabledOpacity={1}
                  onClick={() => handleFontChange(fontPack)}
                >
                  <Container
                    background={isActive ? 'var(--gray-800)' : 'inherit'}
                    className={classNames(
                      'pointer-events-none padding-6 overflow-hidden flex-column jc-space-around gap-8'
                    )}
                    height="100%"
                    width="100%"
                    borderRadius="8px"
                  >
                    <Text
                      clamp={1}
                      color={isActive ? 'var(--gray-100)' : 'var(--gray-300)'}
                      fontSize={'12px'}
                      className="C1 display-inline-block"
                      fontFamily={fontPack?.heading_name}
                      whiteSpace="normal"
                    >
                      {fontPack?.heading_name?.replace(/__custom.*/, '')}
                    </Text>
                    <Text
                      clamp={1}
                      color={isActive ? 'var(--gray-100)' : 'var(--gray-300)'}
                      fontSize={'12px'}
                      className="C1 display-inline-block"
                      fontFamily={fontPack?.body_name}
                      whiteSpace="normal"
                    >
                      {fontPack?.body_name?.replace(/__custom.*/, '')}
                    </Text>
                  </Container>
                </Button>
              );
            }}
          />
        </OptionalComponent>
      </Activity>
      <Activity mode={selectFontsOpen ? 'visible' : 'hidden'}>
        <AddNewFont
          fontPacks={fontPacks}
          handleFontPackCreated={handleFontPackCreated}
          cancelFontPackAdd={cancelFontPackAdd}
        />
      </Activity>
    </Container>
  );
};

export default ThemePackFontVariantChange;
